import React, { useState } from 'react'
import type { ApiError } from '@/shared/api'
import { usePushAudienceCount, useSendPushCampaign } from '@/shared/hooks'
import { Button } from '@/shared/ui'
import {
  PushCampaignForm,
  emptyPushCampaign,
  isPushCampaignValid,
  type PushCampaignValues,
} from './PushCampaignForm'

const FORM_ID = 'push-campaign-form'

interface PushCampaignModalProps {
  onClose: () => void
  /** Рассылка создана: страница обновляет историю и переходит на 1-ю страницу */
  onSent: () => void
}

type Step = 'form' | 'confirm'

/** Модалка новой рассылки: форма → подтверждение с числом получателей */
export const PushCampaignModal: React.FC<PushCampaignModalProps> = ({
  onClose,
  onSent,
}) => {
  const [values, setValues] = useState<PushCampaignValues>(emptyPushCampaign)
  const [step, setStep] = useState<Step>('form')
  const [count, setCount] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const audienceCount = usePushAudienceCount()
  const send = useSendPushCampaign()

  const isValid = isPushCampaignValid(values)
  const isSending = send.isPending

  const handleChange = (patch: Partial<PushCampaignValues>) => {
    setValues((current) => ({ ...current, ...patch }))
    setError(null)
  }

  // Пустую аудиторию отсекаем сами: GET /audience отдаёт 200 с count=0,
  // а 400 «Нет курьеров…» приходит только от создания кампании.
  const handleRequestConfirm = async () => {
    if (!isValid) return
    setError(null)
    try {
      const result = await audienceCount.mutateAsync(values.audience)
      if (result.count === 0) {
        setError('Нет курьеров в выбранной аудитории.')
        return
      }
      setCount(result.count)
      setStep('confirm')
    } catch (requestError) {
      const apiError = requestError as ApiError
      // Интерсептор уже показал тост для 4xx — не дублируем inline-текстом
      if (!apiError?.alreadyToasted) {
        setError(
          apiError?.detail || 'Не удалось определить число получателей'
        )
      }
    }
  }

  const handleSend = async () => {
    try {
      await send.mutateAsync({
        title: values.title.trim(),
        message: values.message.trim(),
        audience: values.audience,
      })
      onSent()
      onClose()
    } catch {
      // Ошибку отправки показывает хук; модалка остаётся открытой для повтора
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="push-campaign-title"
    >
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="space-y-4 p-6">
          <h2
            id="push-campaign-title"
            className="text-xl font-semibold text-gray-900"
          >
            {step === 'form' ? 'Новая рассылка' : 'Подтверждение отправки'}
          </h2>

          {step === 'form' ? (
            <PushCampaignForm
              values={values}
              onChange={handleChange}
              formId={FORM_ID}
              disabled={audienceCount.isPending}
              errorMessage={error}
              onSubmit={() => {
                void handleRequestConfirm()
              }}
            />
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Отправить push-уведомление {count} курьерам?
              </p>
              <div className="rounded-md bg-gray-50 p-3">
                <p className="font-semibold text-gray-900">{values.title}</p>
                <p className="mt-1 text-sm text-gray-600">{values.message}</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t bg-gray-50 p-6">
          {step === 'form' ? (
            <>
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={audienceCount.isPending}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                form={FORM_ID}
                variant="primary"
                disabled={!isValid}
                isLoading={audienceCount.isPending}
              >
                Отправить
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={() => setStep('form')}
                disabled={isSending}
              >
                Назад
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  void handleSend()
                }}
                isLoading={isSending}
              >
                Отправить
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
